import React, { useCallback, useRef } from "react";

import type { NewProductDraftSnapshot, NewProductState, SourceConnection, SourceId } from "../newProduct/state";
import { NewProductScreen, type NewProductFormHandle } from "../newProduct/NewProductScreen";
import {
    addContextMeshSource,
    completeContextMeshCreationDraft,
    ContextMeshApiError,
    createContextMesh,
    type CreateContextMeshRequest,
    type CreateContextMeshSourceRequest
} from "./contextMeshApi";

const GIT_PROVIDERS: Partial<Record<SourceId, string>> = {
    github: "github",
    gitlab: "gitlab",
    bitbucket: "bitbucket",
    azuregit: "azure-repos"
};

const INTEGRATION_PROVIDERS: Partial<Record<SourceId, string>> = {
    jira: "jira",
    azure: "azure-devops"
};

export type StandaloneContextMeshCreationType = "new_product" | "new_features" | "new_single_feature";

export interface PendingStandaloneContextMeshSubmission {
    contextMeshId: string;
    sourceRequests: CreateContextMeshSourceRequest[];
}

export interface StandaloneContextMeshSubmissionDependencies {
    createWorkspace: typeof createContextMesh;
    addSource: typeof addContextMeshSource;
}

const defaultSubmissionDependencies: StandaloneContextMeshSubmissionDependencies = {
    createWorkspace: createContextMesh,
    addSource: addContextMeshSource
};

export class StandaloneContextMeshSourceError extends Error {
    readonly pending: PendingStandaloneContextMeshSubmission;

    constructor(pending: PendingStandaloneContextMeshSubmission) {
        const count = pending.sourceRequests.length;
        super(`${count} Context Mesh source${count === 1 ? "" : "s"} could not be attached. Select Create Context Mesh to retry.`);
        this.name = "StandaloneContextMeshSourceError";
        this.pending = pending;
    }
}

function normalizedRows(rows: string[]): string[] {
    return rows.map(value => value.trim()).filter(Boolean);
}

export function standaloneCreationType(state: NewProductState): StandaloneContextMeshCreationType {
    if (state.kind !== "existing") return "new_product";
    return state.scope === "single" ? "new_single_feature" : "new_features";
}

/**
 * Preserve every discovery answer from the shared creation form on the
 * standalone workspace. The first-class workspace columns feed Context Mesh
 * analysis; metadata retains the creation intent and existing-product context
 * without creating or linking a Product.
 */
export function buildStandaloneContextMeshRequest(state: NewProductState): CreateContextMeshRequest {
    const name = state.fields.name.trim();
    const problem = state.fields.problem.trim();
    const solution = state.fields.solution.trim();
    const personas = normalizedRows(state.users);
    const teamMembers = normalizedRows(state.team);
    const creationType = standaloneCreationType(state);
    const nonfunctionalRequirements = Object.fromEntries(
        Object.entries(state.nfr)
            .map(([key, value]) => [key, value.trim()] as const)
            .filter(([, value]) => Boolean(value))
    );
    const existingProduct = state.kind === "existing"
        ? {
              name: state.existingProduct.name.trim(),
              purpose: state.existingProduct.purpose.trim(),
              users: normalizedRows(state.existingProduct.users),
              capabilities: state.existingProduct.capabilities.trim()
          }
        : null;

    return {
        name,
        description: solution,
        problemStatement: problem,
        solutionStatement: solution,
        personas,
        teamComposition: teamMembers.join("\n"),
        nonfunctionalRequirements,
        metadata: {
            schemaVersion: 1,
            createdFrom: "context_mesh_hub",
            creationType,
            creationMode: "standalone_context_mesh",
            primaryFormFactor: state.primaryFormFactor ?? "web",
            featureScope: state.kind === "existing" ? state.scope : null,
            discovery: {
                name,
                problem,
                solution,
                targetUsers: personas,
                teamMembers,
                ...(existingProduct ? { existingProduct } : {})
            }
        }
    };
}

export function buildStandaloneSourceRequests(
    connections: Record<SourceId, SourceConnection>
): CreateContextMeshSourceRequest[] {
    const requests: CreateContextMeshSourceRequest[] = [];

    for (const fileId of connections.files.staged?.fileIds ?? []) {
        requests.push({ sourceKind: "vector_file", provider: "internal", vectorFileId: fileId });
    }

    for (const source of Object.keys(GIT_PROVIDERS) as SourceId[]) {
        const provider = GIT_PROVIDERS[source];
        if (!provider) continue;
        for (const repositoryId of connections[source].staged?.repoIds ?? []) {
            requests.push({ sourceKind: "git_repository", provider, repositoryId });
        }
    }

    for (const source of Object.keys(INTEGRATION_PROVIDERS) as SourceId[]) {
        const provider = INTEGRATION_PROVIDERS[source];
        if (!provider) continue;
        const connection = connections[source];
        for (const selection of connection.selection ?? []) {
            requests.push({
                sourceKind: "integration",
                provider,
                externalRef: selection.key || selection.id,
                externalName: selection.name,
                configuration: {
                    selectionId: selection.id,
                    ...(selection.key ? { selectionKey: selection.key } : {}),
                    ...(connection.staged?.processJobId ? { processJobId: connection.staged.processJobId } : {})
                },
                integrationMetadata: {
                    importedCount: connection.staged?.importedCount ?? 0
                }
            });
        }
    }

    return requests;
}

/**
 * Create exactly one standalone workspace and attach its staged sources. When
 * an attachment fails, the error carries only the failed requests so a retry
 * resumes the same workspace and cannot duplicate already attached sources.
 */
export async function submitStandaloneContextMesh(
    token: string,
    state: NewProductState,
    pending: PendingStandaloneContextMeshSubmission | null = null,
    dependencies: StandaloneContextMeshSubmissionDependencies = defaultSubmissionDependencies
): Promise<string> {
    let contextMeshId: string;
    let sourceRequests: CreateContextMeshSourceRequest[];

    if (pending) {
        contextMeshId = pending.contextMeshId;
        sourceRequests = pending.sourceRequests;
    } else {
        const workspace = await dependencies.createWorkspace(token, buildStandaloneContextMeshRequest(state));
        contextMeshId = workspace.contextMeshId;
        sourceRequests = buildStandaloneSourceRequests(state.connections);
    }

    const failures: CreateContextMeshSourceRequest[] = [];
    for (const request of sourceRequests) {
        try {
            await dependencies.addSource(token, contextMeshId, request);
        } catch (error) {
            // A lost success response can make a retry encounter the source's
            // uniqueness constraint. The source is already attached, so the
            // retry may safely count that conflict as complete.
            if (error instanceof ContextMeshApiError && error.status === 409) continue;
            console.error("Failed to attach Context Mesh source", error);
            failures.push(request);
        }
    }

    if (failures.length > 0) {
        throw new StandaloneContextMeshSourceError({ contextMeshId, sourceRequests: failures });
    }
    return contextMeshId;
}

export interface StandaloneContextMeshCreationScreenProps {
    token?: string | null;
    /** Resume an existing standalone Context Mesh draft. */
    draftId?: number | null;
    onCancel: () => void;
    onCreated: (contextMeshId: string) => void;
    onDirtyChange?: (dirty: boolean) => void;
    onDraftIdentityChange?: (draftId: number) => void;
    /** Exposes the shared form's live draft to FDE while this route is active. */
    draftRef?: React.MutableRefObject<NewProductDraftSnapshot | null>;
    /** Lets FDE apply a user-confirmed patch through the shared form reducer. */
    formRef?: React.MutableRefObject<NewProductFormHandle | null>;
}

/** Thin offering-specific adapter around the shared Add New Product form. */
export function StandaloneContextMeshCreationScreen({
    token,
    draftId,
    onCancel,
    onCreated,
    onDirtyChange,
    onDraftIdentityChange,
    draftRef,
    formRef
}: StandaloneContextMeshCreationScreenProps) {
    const pendingSubmissionRef = useRef<PendingStandaloneContextMeshSubmission | null>(null);
    // Once mounted, autosave may publish a newly assigned id to the route.
    // Keep the form's resume input fixed so that route update does not reload
    // (or remount) the draft the user is actively editing.
    const resumedDraftIdRef = useRef(draftId ?? null);

    const handleCreate = useCallback(async (
        state: NewProductState,
        persisted: { draftId: number; draftVersion: number }
    ) => {
        if (!token) throw new Error("Your session hasn't finished loading — try again in a moment.");
        try {
            const contextMeshId = await submitStandaloneContextMesh(token, state, pendingSubmissionRef.current);
            // Keep the successfully attached workspace resumable if the final
            // v2 association itself needs a retry. This cannot create a
            // duplicate mesh or reattach completed sources.
            pendingSubmissionRef.current = { contextMeshId, sourceRequests: [] };
            await completeContextMeshCreationDraft(
                token,
                contextMeshId,
                persisted.draftId,
                persisted.draftVersion
            );
            pendingSubmissionRef.current = null;
            return contextMeshId;
        } catch (error) {
            if (error instanceof StandaloneContextMeshSourceError) {
                pendingSubmissionRef.current = error.pending;
            }
            throw error;
        }
    }, [token]);

    return (
        <NewProductScreen
            ref={formRef}
            draftRef={draftRef}
            creationExperience="standalone-context-mesh"
            draftId={resumedDraftIdRef.current}
            onCancel={onCancel}
            onCreated={() => undefined}
            onCreateStandaloneContextMesh={handleCreate}
            onStandaloneContextMeshCreated={onCreated}
            onDirtyChange={onDirtyChange}
            onDraftIdentityChange={onDraftIdentityChange}
        />
    );
}
